/**
 * Contact Form
 * Handles form submission to App Proxy endpoint for contact requests.
 * Collects standard fields (name, email, message) plus dynamic custom
 * fields configured via the theme editor, and posts JSON to the backend.
 */

class ContactForm extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._isSubmitting = false;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;

    this._initialized = true;
    this._abortController = new AbortController();
    this._initElements();
    this._bindEvents();
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    this._initialized = false;
  }

  get endpoint() {
    return this.dataset.endpoint || '/apps/service-request';
  }

  _initElements() {
    this._statusContainer = this.querySelector('[data-status-container]');
    this._successMessage = this.querySelector('[data-success-message]');
    this._errorMessage = this.querySelector('[data-error-message]');
    this._formFields = this.querySelector('[data-form-fields]');
    this._submitContainer = this.querySelector('[data-submit-container]');
    this._submitButton = this._submitContainer?.querySelector('button[type="submit"]');
    this._customFieldsContainer = this.querySelector('[data-custom-fields]');

    this._nameInput = this.querySelector('[name="contact[name]"]');
    this._emailInput = this.querySelector('[name="contact[email]"]');
    this._phoneInput = this.querySelector('[name="contact[phone]"]');
    this._messageInput = this.querySelector('[name="contact[body]"]');
  }

  _bindEvents() {
    const signal = this._abortController.signal;

    if (this._submitButton) {
      this._submitButton.addEventListener('click', (event) => {
        event.preventDefault();
        this._handleSubmit();
      }, { signal });
    }

    // Enter key submits on non-textarea inputs
    const inputs = this.querySelectorAll('input, select');
    inputs.forEach((input) => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this._handleSubmit();
        }
      }, { signal });
    });
  }

  _validateForm() {
    let isValid = true;
    let firstInvalid = null;

    // Name
    if (!this._nameInput || !this._nameInput.value.trim()) {
      isValid = false;
      if (!firstInvalid) firstInvalid = this._nameInput;
    }

    // Email format
    if (!this._emailInput || !this._emailInput.value.trim()) {
      isValid = false;
      if (!firstInvalid) firstInvalid = this._emailInput;
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this._emailInput.value.trim())) {
        isValid = false;
        if (!firstInvalid) firstInvalid = this._emailInput;
      }
    }

    // Phone
    if (!this._phoneInput || !this._phoneInput.value.trim()) {
      isValid = false;
      if (!firstInvalid) firstInvalid = this._phoneInput;
    }

    // Message
    if (!this._messageInput || !this._messageInput.value.trim()) {
      isValid = false;
      if (!firstInvalid) firstInvalid = this._messageInput;
    }

    // Required custom fields
    if (this._customFieldsContainer) {
      const requiredInputs = this._customFieldsContainer.querySelectorAll('[required]');
      requiredInputs.forEach((input) => {
        if (!input.value || !input.value.trim()) {
          isValid = false;
          if (!firstInvalid) firstInvalid = input;
        }
      });
    }

    if (!isValid && firstInvalid) {
      firstInvalid.focus();
      firstInvalid.reportValidity();
    }

    return isValid;
  }

  _getFormData() {
    const data = {
      form_type: 'contact',
      name: this._nameInput ? this._nameInput.value.trim() : '',
      email: this._emailInput ? this._emailInput.value.trim() : '',
      phone: this._phoneInput ? this._phoneInput.value.trim() : '',
      message: this._messageInput ? this._messageInput.value.trim() : ''
    };

    // Collect dynamic custom fields
    const customFields = {};
    if (this._customFieldsContainer) {
      const fields = this._customFieldsContainer.querySelectorAll('input, select, textarea');
      fields.forEach((field) => {
        const name = field.getAttribute('name') || '';
        const match = name.match(/^contact\[(.+)\]$/);
        if (match && field.value) {
          customFields[match[1]] = field.value.trim();
        }
      });
    }

    if (Object.keys(customFields).length > 0) {
      data.custom_fields = customFields;
    }

    return data;
  }

  async _handleSubmit() {
    if (this._isSubmitting) return;
    if (!this._validateForm()) return;

    this._isSubmitting = true;
    this._setLoadingState(true);
    this._hideMessages();

    const formData = this._getFormData();

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        this._pushGenerateLead(formData);
        this._showSuccess();
      } else {
        this._showError(result.error || result.message);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Contact form submission error:', error);
      this._showError();
    } finally {
      this._isSubmitting = false;
      this._setLoadingState(false);
    }
  }

  _normalizePhoneNL(phone) {
    if (!phone) return '';
    var cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
    if (cleaned.indexOf('0031') === 0) cleaned = '+31' + cleaned.substring(4);
    else if (cleaned.indexOf('00') === 0) cleaned = '+' + cleaned.substring(2);
    else if (cleaned.indexOf('0') === 0) cleaned = '+31' + cleaned.substring(1);
    else if (cleaned.indexOf('+') !== 0) cleaned = '+31' + cleaned;
    return cleaned;
  }

  _splitName(fullName) {
    if (!fullName) return { first_name: '', last_name: '' };
    var trimmed = fullName.trim();
    var lastSpace = trimmed.lastIndexOf(' ');
    if (lastSpace === -1) return { first_name: trimmed, last_name: '' };
    return {
      first_name: trimmed.substring(0, lastSpace),
      last_name: trimmed.substring(lastSpace + 1)
    };
  }

  _pushGenerateLead(formData) {
    try {
      var names = this._splitName(formData.name);
      var normalizedPhone = this._normalizePhoneNL(formData.phone);
      var attribution = (window.CRAttribution && window.CRAttribution.capture)
        ? window.CRAttribution.capture()
        : {};

      var payload = {
        event: 'generate_lead_stape',
        value: 50,
        currency: 'EUR',
        lead_source: 'contact_form',
        form_name: 'contact_form',
        user_data: {
          email_address: formData.email,
          phone_number: normalizedPhone,
          first_name: names.first_name,
          last_name: names.last_name
        }
      };
      Object.keys(attribution).forEach(function (k) { payload[k] = attribution[k]; });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      console.error('generate_lead tracking error:', e);
    }
  }

  _setLoadingState(isLoading) {
    if (this._submitButton) {
      this._submitButton.disabled = isLoading;
      this._submitButton.setAttribute('aria-busy', isLoading.toString());
    }
  }

  _hideMessages() {
    if (this._statusContainer) this._statusContainer.hidden = true;
    if (this._successMessage) this._successMessage.hidden = true;
    if (this._errorMessage) this._errorMessage.hidden = true;
  }

  _showSuccess() {
    if (this._formFields) this._formFields.hidden = true;
    if (this._submitContainer) this._submitContainer.hidden = true;
    if (this._statusContainer) this._statusContainer.hidden = false;
    if (this._successMessage) {
      this._successMessage.hidden = false;
      this._successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _showError(message) {
    if (this._statusContainer) this._statusContainer.hidden = false;
    if (this._errorMessage) {
      this._errorMessage.hidden = false;
      if (message) {
        const bannerContent = this._errorMessage.querySelector('.banner');
        if (bannerContent) {
          const textNode = Array.from(bannerContent.childNodes).find(
            (node) => node.nodeType === Node.TEXT_NODE || node.tagName !== 'SVG'
          );
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            textNode.textContent = message;
          }
        }
      }
      this._errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

if (!window.customElements.get('contact-form')) {
  window.customElements.define('contact-form', ContactForm);
}
